import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { FiX, FiSearch, FiCalendar, FiRefreshCw, FiAlertTriangle } from "react-icons/fi";
import { fetchTaskPool, pinTaskToDay } from "../../../utils/taskApi";
import {
  TASK_TYPE_LABEL,
  TASK_TYPE_BADGE,
  POOL_STATUS_LABEL,
  POOL_STATUS_BADGE,
  formatDay,
  utcDay,
} from "../taskConstants";

/**
 * Pin a task onto a day, by hand.
 *
 * The override exists because the selection rules cannot know everything — a
 * partner launch, a task that has to run on a particular date. So this
 * deliberately lets you pick a task the generator would have skipped, and says
 * so, rather than hiding cooling-down tasks and leaving the admin to wonder
 * where they went.
 *
 * Opened either from a pool row (task fixed, pick the day) or from a schedule
 * column (day fixed, pick the task).
 */

const PinTaskModal = ({ isOpen, onClose, onPinned, fixedTask = null, fixedDay = null }) => {
  const [day, setDay] = useState(fixedDay || utcDay(0));
  const [selectedTask, setSelectedTask] = useState(fixedTask);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pinning, setPinning] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setDay(fixedDay || utcDay(0));
    setSelectedTask(fixedTask);
    setSearch("");
    setDebouncedSearch("");
  }, [isOpen, fixedDay, fixedTask]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchTaskPool({ page: 1, limit: 50, search: debouncedSearch });
      // Archived tasks are refused by the API, so they are not offered here.
      setRows((res.data || []).filter((t) => t.status !== "archived"));
    } catch (err) {
      toast.error(err.message || "Failed to load the pool");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (isOpen && !fixedTask) loadTasks();
  }, [isOpen, fixedTask, loadTasks]);

  if (!isOpen) return null;

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(val), 400);
  };

  const handlePin = async () => {
    if (!selectedTask) return;
    setPinning(true);
    try {
      const res = await pinTaskToDay(day, selectedTask.id);
      toast.success(res.message || "Task pinned to the schedule");
      if (onPinned) onPinned(res.data);
      onClose();
    } catch (err) {
      // The API refuses for reasons worth reading — already on that day, an
      // archived task, a day that has passed — so its own words are shown.
      toast.error(err.message || "Failed to pin the task");
    } finally {
      setPinning(false);
    }
  };

  const isPast = day < utcDay(0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <FiCalendar size={16} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">Pin a task to a day</h2>
              <p className="text-[11px] text-gray-400">
                Skips weight, cooldown and the per-type cap — but never publishes the same task twice on one
                day.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 transition">
            <FiX size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Day (UTC)</label>
            <input
              type="date"
              value={day}
              min={utcDay(0)}
              onChange={(e) => setDay(e.target.value)}
              disabled={!!fixedDay}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50 disabled:text-gray-500"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              {formatDay(day)}
              {day === utcDay(0) && " — pinned onto today it goes live immediately."}
            </p>
          </div>

          {isPast && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-start gap-2">
              <FiAlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={15} />
              <p className="text-xs text-rose-800">That day has already passed — pick today or later.</p>
            </div>
          )}

          {fixedTask ? (
            <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/60">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Task</p>
              <p className="font-semibold text-gray-800 text-sm">{fixedTask.title}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${TASK_TYPE_BADGE}`}>
                  {TASK_TYPE_LABEL[fixedTask.task_type] || fixedTask.task_type}
                </span>
                <span className="text-[11px] text-gray-500">{fixedTask.reward_points} points</span>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Task</label>
              <div className="relative mb-2">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search the pool..."
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>

              <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-64 overflow-y-auto">
                {loading ? (
                  <div className="px-4 py-8 text-center text-gray-400 text-xs flex flex-col items-center gap-2">
                    <FiRefreshCw className="animate-spin text-blue-600" size={18} />
                    Loading the pool...
                  </div>
                ) : rows.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-400 text-xs">
                    Nothing in the pool matches that.
                  </div>
                ) : (
                  rows.map((task) => (
                    <button
                      key={task.id}
                      onClick={() => setSelectedTask(task)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center justify-between gap-3 ${
                        selectedTask?.id === task.id ? "bg-blue-50/70" : ""
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 text-xs truncate">{task.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${TASK_TYPE_BADGE}`}>
                            {TASK_TYPE_LABEL[task.task_type] || task.task_type}
                          </span>
                          <span className="text-[10px] text-gray-400">{task.reward_points} pts</span>
                        </div>
                      </div>
                      <span
                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full border shrink-0 ${
                          POOL_STATUS_BADGE[task.status] || POOL_STATUS_BADGE.draft
                        }`}
                      >
                        {POOL_STATUS_LABEL[task.status] || task.status}
                      </span>
                    </button>
                  ))
                )}
              </div>

              {selectedTask && selectedTask.status !== "ready" && (
                <p className="text-[11px] text-amber-700 mt-2">
                  This task is <span className="font-semibold">{POOL_STATUS_LABEL[selectedTask.status]}</span> —
                  the generator would never pick it. Pinning publishes it anyway.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={handlePin}
            disabled={pinning || !selectedTask || isPast}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
          >
            {pinning ? "Pinning..." : "Pin to day"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PinTaskModal;
